import type { Eixo, SemEixo } from './eixo'
import { textoSemEixo } from './eixo'
import { nomeDaClasse } from './escolha'
import type { Veredicto } from './geometria'

/** Como se diz cada resultado, no painel e no CSV. Separado do painel
 *  para os dois dizerem o mesmo, e para se poder testar. */

/** Milímetros, sem casas decimais acima de 10 e com uma abaixo. Vírgula
 *  decimal, como se escreve cá. */
export function mm(metros: number): string {
  const v = metros * 1000
  const t = Math.abs(v) >= 10 ? Math.round(v).toString() : v.toFixed(1)
  return `${t.replace('.', ',')} mm`
}

export function percentagem(fraccao: number): string {
  return `${Math.round(fraccao * 100)}%`
}

export const NOME_DO_VEREDICTO: Record<Veredicto, string> = {
  conforme: 'Conforme',
  desvio: 'Com desvio',
  'nao-visto': 'Não visto',
}

function virgula(v: number, casas: number): string {
  return v.toFixed(casas).replace('.', ',')
}

/** A incerteza com as casas que ela pede: "± 0,0" lia-se como
 *  certeza absoluta, e é só pequena. */
function incerteza(v: number): string {
  return virgula(v, v < 0.1 ? 2 : 1)
}

/** Um valor menor do que duas vezes a sua incerteza não se distingue
 *  do ruído, e diz-se. Uma cantoneira pequena com poucos pontos dava
 *  3,6 mm/m de desaprumo sem ter mexido. */
function dentroDoRuido(valor: number, sigma: number): string {
  return sigma > 0 && Math.abs(valor) < 2 * sigma ? ' (dentro do ruído)' : ''
}

/** Uma frase sobre o eixo: o que interessa a quem fiscaliza. */
export function textoDoEixo(eixo: Eixo | SemEixo | null): string {
  if (eixo === null) return ''
  if (typeof eixo === 'string') return textoSemEixo(eixo)
  const partes: string[] = []
  const sigmaAng = eixo.incertezaDesaprumoMmPorM
  if (eixo.orientacao === 'vertical' && eixo.desaprumoMmPorM !== null) {
    partes.push(
      `desaprumo ${virgula(eixo.desaprumoMmPorM, 1)} ± ${incerteza(sigmaAng)} mm/m` +
        dentroDoRuido(eixo.desaprumoMmPorM, sigmaAng),
    )
  } else {
    const mmPorM = Math.tan((eixo.desvioAngular * Math.PI) / 180) * 1000
    partes.push(
      `inclinação ${virgula(mmPorM, 1)} ± ${incerteza(sigmaAng)} mm/m em relação ao projecto` +
        dentroDoRuido(mmPorM, sigmaAng),
    )
  }
  if (eixo.direccoesVistas === 0) {
    partes.push('fuga lateral por conhecer')
  } else {
    partes.push(
      `fuga ${mm(eixo.deslocamentoTotal)} ± ${mm(eixo.incertezaFuga)}` +
        (eixo.direccoesVistas === 1 ? ' (só numa direcção, uma face vista)' : ''),
    )
  }
  return partes.join(', ')
}

export interface LinhaParaCsv {
  localId: number
  nome: string | null
  classe: string
  veredicto: Veredicto
  pontos: number
  media: number
  rms: number
  p95: number
  max: number
  dentro: number
  eixo: Eixo | SemEixo | null
}

function campo(v: string): string {
  return /[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

function numero(v: number | null | undefined, casas: number): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return ''
  return v.toFixed(casas).replace('.', ',')
}

/** O relatório em CSV com ponto e vírgula, que é o que o Excel em
 *  português abre sem perguntar nada. Distâncias em milímetros. */
export function relatorioCsv(linhas: LinhaParaCsv[], toleranciaM: number): string {
  const cab = [
    'Id local',
    'Nome',
    'Classe',
    'Resultado',
    'Pontos',
    'Média (mm)',
    'RMS (mm)',
    'P95 (mm)',
    'Máximo (mm)',
    `Dentro de ${numero(toleranciaM * 1000, 0)} mm (%)`,
    'Desaprumo (mm/m)',
    'Incerteza do desaprumo (mm/m)',
    'Desvio do eixo (°)',
    'Fuga lateral (mm)',
    'Incerteza da fuga (mm)',
    'Direcções vistas',
    'Nota do eixo',
  ]
  const corpo = linhas.map((l) => {
    const e = l.eixo && typeof l.eixo !== 'string' ? l.eixo : null
    return [
      String(l.localId),
      campo(l.nome ?? ''),
      nomeDaClasse(l.classe),
      NOME_DO_VEREDICTO[l.veredicto],
      String(l.pontos),
      numero(l.media * 1000, 1),
      numero(l.rms * 1000, 1),
      numero(l.p95 * 1000, 1),
      numero(l.max * 1000, 1),
      numero(l.dentro * 100, 0),
      numero(e?.desaprumoMmPorM, 2),
      numero(e?.incertezaDesaprumoMmPorM, 2),
      numero(e?.desvioAngular, 3),
      numero(e ? e.deslocamentoTotal * 1000 : null, 1),
      numero(e ? e.incertezaFuga * 1000 : null, 1),
      e ? String(e.direccoesVistas) : '',
      campo(typeof l.eixo === 'string' ? textoSemEixo(l.eixo) : ''),
    ].join(';')
  })
  // O BOM faz o Excel ler os acentos como UTF-8.
  return String.fromCharCode(0xfeff) + [cab.join(';'), ...corpo].join('\r\n') + '\r\n'
}
